import * as DG from "ui/dialogs"
import { MouseEvent, useState, ChangeEvent } from "react"

import Button from "ui/buttons/Button"
import { ReactComponent as DeleteIcon } from "assets/delete-grey.svg"
import { IconButton } from "@material-ui/core"
import { Market } from "models/regions"
import { MediaField } from "../../GlobalMediaTypes/types"
import { TextInput } from "components/inputs/TextInput"
import { WithAuthority } from "components/WithAuthority"
import { css } from "@emotion/css"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

export type OptionsListModalProps = {
  open: boolean
  onClose: () => void
  data: string[]
  onUpdate: (
    row: MediaField | Market,
    columnKey: string,
    value: string | Array<any>,
  ) => void
  row: MediaField | Market
}

export function OptionsListModal(props: OptionsListModalProps) {
  const { open, onClose, data, onUpdate, row } = props
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const [options, setOptions] = useState<string[]>(() =>
    data?.length ? [...data] : [""],
  )
  const handleChange =
    (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined) =>
    (index: number) => {
      const { value } = evt?.target ?? {}
      setOptions((s) => {
        let _options = [...s]
        _options.splice(index, 1, value || "")
        return _options
      })
    }

  const handleClose = () => {
    setOptions(() => (data?.length ? [...data] : [""]))
    onClose()
  }

  const addRow = () => {
    setOptions((s) => [...s, ""])
  }
  const deleteRow = (_: MouseEvent<SVGSVGElement>, index: number) => {
    setOptions((s) => {
      let _options = [...s]
      _options.splice(index, 1)
      return _options
    })
  }

  const handleSubmit = () => {
    onUpdate(row, "options", options)
    onClose()
  }

  const id = "options-list-dialog"
  return (
    <DG.Dialog
      fullWidth={false}
      open={open}
      onClose={onClose}
      id={id}
      className={styles.dialog}
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id} className="BodyBody-1BlackBold">
          {t("admin.mediaFields.type.addList.title")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={onClose} />
      </DG.DialogHeader>

      <DG.DialogContent
        center
        paddingTop
        className={
          hasAuthority(["AUTH_EDIT_MEDIA_FIELD"])
            ? styles.containerWithButtons
            : styles.container
        }
      >
        {options?.length &&
          options.map((o, idx) => (
            <div key={idx} className={styles.inputRow}>
              <TextInput
                variant="outlined"
                color="primary"
                size="medium"
                margin="normal"
                value={o || ""}
                onChange={(e) => handleChange(e)(idx)}
                label={
                  !!o.length
                    ? t("admin.mediaFields.type.addList.option")
                    : t("admin.mediaFields.type.addList.label")
                }
                placeholder={t("admin.mediaFields.type.addList.placeholder")}
                className={styles.input}
                disabled={!hasAuthority(["AUTH_EDIT_MEDIA_FIELD"])}
                // error={Boolean(errors.approver)}
                // helperText={errors.approver}
              />
              <WithAuthority allowed={["AUTH_EDIT_MEDIA_FIELD"]}>
                <IconButton className={styles.iconButton}>
                  <DeleteIcon
                    onClick={(e: MouseEvent<SVGSVGElement>) =>
                      deleteRow(e, idx)
                    }
                    className={styles.deleteIcon}
                  />
                </IconButton>
              </WithAuthority>
            </div>
          ))}
        <WithAuthority allowed={["AUTH_EDIT_MEDIA_FIELD"]}>
          <Button
            onClick={addRow}
            color="primary"
            className={styles.textButton}
          >
            {t("admin.mediaFields.type.addList.add")}
          </Button>
        </WithAuthority>
      </DG.DialogContent>
      {hasAuthority(["AUTH_EDIT_MEDIA_FIELD"]) ? (
        <DG.DialogFooter>
          <DG.DialogActions>
            <Button variant="outlined" color="primary" onClick={handleClose}>
              {t("modal.label.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={handleSubmit}
              disabled={!hasAuthority(["AUTH_EDIT_MEDIA_FIELD"])}
              // disabled={
              //   status === "loading"
              // }
            >
              {t("admin.mediaFields.type.addList.apply")}
            </Button>
          </DG.DialogActions>
        </DG.DialogFooter>
      ) : null}
    </DG.Dialog>
  )
}

const styles = {
  container: css`
    min-width: 467px;
    max-height: 640px;
    overflow: auto;
    margin: 0 var(--spacing-xs) var(--spacing-xs) 0;
  `,
  containerWithButtons: css`
    min-width: 467px;
    max-height: 640px;
    overflow: auto;
    margin: 0 var(--spacing-xs) 0 0;
  `,
  inputRow: css`
    display: flex;
    padding-right: var(--spacing-xxl-7);
    align-items: center;

    :first-child {
      > svg {
        display: none;
      }
    }
  `,
  input: css`
    min-width: 280px;
  `,
  deleteIcon: css`
    width: var(--spacing-xl);
    height: var(--spacing-xl);
    cursor: pointer;
    color: var(--grey);
  `,
  iconButton: css`
    &.MuiIconButton-root {
      margin-left: var(--spacing-s);
      margin-top: var(--spacing-xs);
    }
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 14px;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;

    &:focus {
      box-shadow: none;
    }
  `,
  dialog: css`
    *::-webkit-scrollbar {
      width: 6px;
      border-radius: 4px;
      background: var(--primary-light);
    }
    *::-webkit-scrollbar-thumb {
      width: 6px;
      height: var(--spacing-xl);
      border-radius: 4px;
      background: var(--primary-main);
    }
  `,
}
